import { imageSizeMap } from "../../content/configuration";
import { Configuration, Section } from "../../resources/modelTypes";
import {
  getPhotoByPk,
  getVideoByPk,
  RootState,
} from "../../resources/reduxTypes";
import { hasProcessed } from "../../website/components/ContentSection";
import { cloudinaryUrl } from "../../website/utils";
import { css } from "@emotion/react";
import React, { memo } from "react";
import { connect } from "react-redux";

const styles = {
  container: css`
    text-align: center;
  `,
  imageStyles: css`
    box-sizing: border-box;
    display: block;
    height: auto;
    width: 100%;
  `,
  registryImageStyles: css`
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
    object-fit: contain;
    height: 100%;
    width: 100%;
  `,
};

export type DisplayPhotoOrVideoProps = {
  isRegistryPhoto?: boolean;
  configuration: Configuration;
  section: Section;
  defaultImage?: string;
};

export type DisplayPhotoOrVideoPropsWithRedux = {
  hasProcessed?: boolean;
  mediaType: "photo" | "video";
  original: string;
  videoUrl?: string;
} & DisplayPhotoOrVideoProps;

const DisplayPhotoOrVideo = ({
  configuration,
  hasProcessed = false,
  isRegistryPhoto = false,
  mediaType,
  original,
  videoUrl,
  defaultImage,
}: DisplayPhotoOrVideoPropsWithRedux) => {
  const registryImageSrc = original || defaultImage;
  const isVideo = mediaType === "video";

  return (
    <>
      {isRegistryPhoto ? (
        <img css={styles.registryImageStyles} src={registryImageSrc} />
      ) : (
        <div css={[styles.container, imageSizeMap(configuration.imageSize)]}>
          {isVideo && hasProcessed ? (
            <video
              controls
              css={styles.imageStyles}
              playsInline
              poster={original}
              src={videoUrl}
            />
          ) : (
            <img css={styles.imageStyles} src={original} />
          )}
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state: RootState, props: DisplayPhotoOrVideoProps) => {
  if (props.section.video) {
    const video = getVideoByPk(state, props.section.video);

    return {
      hasProcessed: hasProcessed(video?.uploadedAt || video?.createdAt),
      mediaType: "video" as const,
      original: video
        ? cloudinaryUrl(video.publicId, "jpg", "so_auto/h_2048,w_2048,c_fit")
        : "",
      videoUrl: video ? cloudinaryUrl(video.publicId, "mp4", "q_auto") : "",
    };
  }

  if (props.section.image) {
    return {
      mediaType: "photo" as const,
      original: props.section.image,
    };
  }

  return {
    mediaType: "photo" as const,
    original: getPhotoByPk(state, props.section.photo)?.original || "",
  };
};

const ConnectedDisplayPhotoOrVideo = connect(mapStateToProps)(
  DisplayPhotoOrVideo,
) as unknown as React.FC<DisplayPhotoOrVideoProps>;

const MemoizedDisplayPhotoOrVideo = memo(ConnectedDisplayPhotoOrVideo);

export { MemoizedDisplayPhotoOrVideo as DisplayPhotoOrVideo };
